import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { Container } from "reactstrap";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import axiosInstance from "../../../Utils/axiosInstance";

/* ------------------ API HOOKS ------------------ */
const useGetContract_Name = (unit: string) => {
  return useQuery<any>({
    queryKey: ["Contract_Name", unit],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/paper-app/hedging/`, {
        params: { trade_unit: unit },
      });
      return data; // ✅ returns {dict1:[], dict2:[]}
    },
    enabled: !!unit,
    refetchOnWindowFocus: false,
  });
};

const useGetCustomerContract_Name = (unit: string, customer: string) => {
  return useQuery<any>({
    queryKey: ["customerContract_Name", unit, customer],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/paper-app/hedging/`, {
        params: { trade_unit: unit, cust_book: customer },
      });
      return data; // ✅ returns {dict1:[], dict2:[]}
    },
    enabled: !!unit && !!customer,
    refetchOnWindowFocus: false,
  });
};

const useGetCustomers = () => {
  return useQuery<any[]>({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/paper-app/counterparty-companies`
      );
      return data.results || [];
    },
    refetchOnWindowFocus: false,
  });
};

/* ------------------ TABLE COMPONENT ------------------ */
const DataTable: React.FC<{
  data: any[];
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
}> = ({ data, isError, isFetching, isLoading }) => {
  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    if (!data?.length) return [];

    const keys = Object.keys(data[0]);
    const sortedKeys = [
      keys.includes("Contract_Name") ? "Contract_Name" : "Contracts",
      "HLD",
      ...keys.filter(
        (k) =>
          k !== "Contract_Name" &&
          k !== "Contracts" &&
          k !== "HLD" &&
          k !== "Monthly_Total"
      ),
      "Monthly_Total",
    ];

    return sortedKeys.map((key) => ({
      accessorKey: key,
      header:
        key === "Contract_Name" || key === "Contracts" ? "Contracts" : key,
      Cell: ({ cell }) => {
        const value = cell.getValue();

        if (value === "WKND") {
          return <Box sx={{ color: "#999", fontSize: "0.7rem" }}>{value}</Box>;
        }

        if (typeof value === "number") {
          const color = value < 0 ? "#d32f2f" : value > 0 ? "#2e7d32" : "#555";
          return (
            <Box sx={{ color, fontWeight: 500, fontSize: "0.75rem" }}>
              {cell.getValue() as number}
            </Box>
          );
        }

        return <>{String(value)}</>;
      },
    }));
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnPinning: true,
    enableColumnOrdering: true,
    enableColumnDragging: true,
    enableColumnResizing: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableExpanding: true,
    enableFilters: true,
    enableColumnFilters: true,
    enablePagination: false, // or this, depending on MRT version
    enableBottomToolbar: false,
    getRowId: (row) =>
      row.Contract_Name || row.Contracts || row.id || JSON.stringify(row),
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e0",
        fontWeight: 600,
        fontSize: "0.75rem",
        background: "linear-gradient(to right, #f5f7fa, #e6ebf0)",
        color: "#333",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.7rem",
        padding: "4px 8px",
        borderBottom: "1px solid #f1f1f1",
        "&:hover": {
          backgroundColor: "#fafafa", // row hover effect
        },
      },
    },

    muiTopToolbarProps: {
      className: "top-toolbar-wrapper__new",
    },
    muiBottomToolbarProps: {
      className: "bottom-toolbar-wrapper__new",
    },

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 2,
        border: "1px solid #dcdcdc",
        backgroundColor: "#ffffff",
      },
    },
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "#e3f2fd",
        color: "#1976d2",
        fontSize: "0.75rem",
      },
    },
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    initialState: {
      density: "compact",
      showColumnFilters: false,
      grouping: [],
      expanded: true,
    },
  });

  return (
    <Box sx={{ mb: 2 }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

/* ------------------ MAIN APP ------------------ */
const App: React.FC = () => {
  const [tab, setTab] = useState(0);

  // Hedging
  const [unit, setUnit] = useState<string>("lots");
  const {
    data: hedgingData = { dict1: [], dict2: [] },
    isError: hedgingError,
    isFetching: hedgingFetching,
    isLoading: hedgingLoading,
  } = useGetContract_Name(unit);

  // Customer
  const [custUnit, setCustUnit] = useState<string>("");
  const [customer, setCustomer] = useState<string>("");
  const {
    data: customerData = { dict1: [], dict2: [] },
    isError: customerError,
    isFetching: customerFetching,
    isLoading: customerLoading,
  } = useGetCustomerContract_Name(custUnit, customer);

  const { data: customerOptions = [] } = useGetCustomers();

  return (
    <div
      className="page-body"
      style={{ backgroundColor: "#fafafa", minHeight: "100vh" }}
    >
      <Container fluid>
        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            px: 1,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                minHeight: "36px",
                px: 2,
                mx: 0.5,
              },
              "& .Mui-selected": {
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
              },
            }}
          >
            <Tab label="Company Exposure" />
            <Tab label="Customer Exposure" />
          </Tabs>
        </Paper>

        {/* Hedging Tab */}
        {tab === 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
              <FormControl sx={{ minWidth: 100 }} size="small">
                <InputLabel id="unit-select-label">Unit</InputLabel>
                <Select
                  labelId="unit-select-label"
                  value={unit}
                  onChange={(e: SelectChangeEvent) => setUnit(e.target.value)}
                  label="Unit"
                  sx={{
                    fontSize: "0.7rem",
                    height: "28px",
                    ".MuiSelect-select": {
                      py: 0.3,
                    },
                  }}
                >
                  <MenuItem value="kbbl" sx={{ fontSize: "0.75rem" }}>
                    kbbl
                  </MenuItem>
                  <MenuItem value="lots" sx={{ fontSize: "0.75rem" }}>
                    Lots
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Typography
              variant="body2"
              fontWeight={500}
              align="left" // 👈 this forces left alignment
              sx={{ color: "#2c3e50", mb: 0.5 }}
            >
              Paper Exposure
            </Typography>

            {/* Tables stacked vertically */}
            <DataTable
              data={hedgingData.dict1}
              isError={hedgingError}
              isFetching={hedgingFetching}
              isLoading={hedgingLoading}
            />
            <Typography
              variant="body2"
              fontWeight={500}
              align="left" // 👈 this forces left alignment
              sx={{ color: "#2c3e50", mb: 0.5 }}
            >
              {" "}
              Paper Exposure Break By Book & Strategy
            </Typography>
            <DataTable
              data={hedgingData.dict2}
              isError={hedgingError}
              isFetching={hedgingFetching}
              isLoading={hedgingLoading}
            />
          </>
        )}

        {/* Customer Tab */}
        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
              {/* Unit Filter */}
              <FormControl sx={{ minWidth: 100 }} size="small">
                <InputLabel id="cust-unit-label" sx={{ fontSize: "0.7rem" }}>
                  Unit
                </InputLabel>
                <Select
                  labelId="cust-unit-label"
                  value={custUnit}
                  onChange={(e: SelectChangeEvent) =>
                    setCustUnit(e.target.value)
                  }
                  label="Unit"
                  sx={{
                    fontSize: "0.7rem",
                    height: "28px",
                    ".MuiSelect-select": {
                      py: 0.3,
                    },
                  }}
                >
                  <MenuItem value="kbbl" sx={{ fontSize: "0.7rem", py: 0.5 }}>
                    kbbl
                  </MenuItem>
                  <MenuItem value="lots" sx={{ fontSize: "0.7rem", py: 0.5 }}>
                    Lots
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Customer Filter */}
              <FormControl
                sx={{ minWidth: 100 }}
                size="small"
                disabled={!customerOptions.length}
              >
                <InputLabel
                  id="customer-label"
                  sx={{ fontSize: "0.7rem", top: "-4px" }}
                >
                  Customer
                </InputLabel>
                <Select
                  labelId="customer-label"
                  value={customer}
                  onChange={(e: SelectChangeEvent) =>
                    setCustomer(e.target.value)
                  }
                  label="Customer"
                  sx={{
                    fontSize: "0.7rem",
                    height: "28px",
                    ".MuiSelect-select": {
                      py: 0.3,
                    },
                  }}
                >
                  {customerOptions.map((c: any) => (
                    <MenuItem
                      key={c.id}
                      value={c.customer_company_name}
                      sx={{ fontSize: "0.7rem", py: 0.5 }}
                    >
                      {c.customer_company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Typography
              variant="body2"
              fontWeight={500}
              align="left" // 👈 this forces left alignment
              sx={{ color: "#2c3e50", mb: 0.5 }}
            >
              Paper Exposure
            </Typography>

            {/* Tables stacked vertically */}
            <DataTable
              data={customerData.dict1}
              isError={customerError}
              isFetching={customerFetching}
              isLoading={customerLoading}
            />
            <Typography
              variant="body2"
              fontWeight={500}
              align="left" // 👈 this forces left alignment
              sx={{ color: "#2c3e50", mb: 0.5 }}
            >
              {" "}
              Paper Exposure Break By Book & Strategy
            </Typography>
            <DataTable
              data={customerData.dict2}
              isError={customerError}
              isFetching={customerFetching}
              isLoading={customerLoading}
            />
          </>
        )}
      </Container>
    </div>
  );
};

/* ------------------ APP WRAPPER ------------------ */
const queryClient = new QueryClient();

export default function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
}
